if (!customElements.get('ctm-design-studio')) {
  class CtmDesignStudioA11y {
    constructor(root) {
      this.root = root;
    }

    syncRootState({ hasDesigns = false, isUploading = false, atLimit = false } = {}) {
      this.root.classList.toggle('is-empty', !hasDesigns);
      this.root.classList.toggle('has-designs', hasDesigns);
      this.root.classList.toggle('is-uploading', isUploading);
      this.root.classList.toggle('is-limit-reached', atLimit);
    }

    syncCanvas({ canvas, imagePreview, hasDesigns = false, isBusy = false }) {
      canvas.toggleAttribute('aria-busy', isBusy);
      imagePreview.hidden = !hasDesigns;
    }

    syncUploadControls({ fileInput, uploadTriggers, libraryUploadButton, isBusy = false, atLimit = false, hasDesigns = false }) {
      fileInput.disabled = isBusy || atLimit;
      uploadTriggers.forEach((button) => {
        button.disabled = isBusy || atLimit;
        button.toggleAttribute('aria-busy', isBusy);
      });
      if (libraryUploadButton) {
        libraryUploadButton.hidden = !hasDesigns;
        libraryUploadButton.disabled = isBusy || atLimit;
      }
    }

    syncDesignAvailability(inputs, hasDesigns, submitUrl = '', requireAgreement = true) {
      inputs.scaleRange.disabled = !hasDesigns;
      if (inputs.fitPreviewButton) inputs.fitPreviewButton.disabled = !hasDesigns;
      inputs.termsInput.disabled = !hasDesigns || !requireAgreement;
      inputs.reviewButton.disabled = !hasDesigns || !submitUrl || (requireAgreement && !inputs.termsInput.checked);
    }

    syncActiveDesignCard(card, { active = false, displayName = '', index = 0, total = 0 } = {}) {
      if (!card) return;
      const previewButton = card.querySelector('[data-design-select]');
      const deleteButton = card.querySelector('[data-design-delete]');
      if (previewButton) previewButton.setAttribute('aria-label', `Preview ${displayName}, ${index + 1} of ${total}`);
      if (deleteButton) deleteButton.setAttribute('aria-label', `Delete ${displayName}`);
      if (active) {
        card.setAttribute('aria-current', 'true');
      } else if (card.hasAttribute('aria-current')) {
        card.removeAttribute('aria-current');
      }
    }

    syncIntentInputs(inputs, value, copyEl, termsLink, termsText) {
      inputs.forEach((input) => {
        input.checked = input.value === value;
      });

      if (copyEl) {
        const copyKey = value === 'public' ? 'publicIntentCopy' : 'privateIntentCopy';
        copyEl.textContent = copyEl.dataset[copyKey] || '';
      }

      if (termsLink && termsText) {
        const termsKey = value === 'public' ? 'publicTermsUrl' : 'privateTermsUrl';
        const termsUrl = termsLink.dataset[termsKey] || '';
        termsLink.hidden = !termsUrl;
        termsText.hidden = Boolean(termsUrl);

        if (termsUrl) {
          termsLink.href = termsUrl;
        } else {
          termsLink.removeAttribute('href');
        }
      }
    }

    syncFamilyInputs(inputs, value) {
      inputs.forEach((input) => {
        input.checked = input.value === value;
      });
    }

    syncPreviewImage(previewImage, design, hasDesigns, previewUrl = '') {
      if (!hasDesigns || !design) {
        previewImage.removeAttribute('src');
        previewImage.alt = '';
        return;
      }

      previewImage.src = previewUrl || design.uploadedImageUrl;
      previewImage.alt = `Preview of ${design.name || 'Untitled design'}`;
    }

    syncPreviewButtons(buttons, activeMode) {
      buttons.forEach((button) => {
        const isActive = button.dataset.previewView === activeMode;
        button.setAttribute('aria-pressed', String(isActive));
      });
    }

    syncDesignCount(designCount, count) {
      designCount.textContent = `${count} ${count === 1 ? 'design' : 'designs'}`;
    }

    syncStatus(region, message, state = '') {
      region.textContent = message;
      if (state) {
        region.dataset.state = state;
      } else {
        delete region.dataset.state;
      }
    }

    syncScaleHelp({ group, trigger, panel }, isOpen = false) {
      if (!group || !trigger || !panel) return;
      group.classList.toggle('is-help-open', isOpen);
      trigger.setAttribute('aria-expanded', String(isOpen));
      panel.hidden = !isOpen;
    }

  }

  class CtmDesignStudio extends HTMLElement {
    constructor() {
      super();
      this.a11y = new CtmDesignStudioA11y(this);
      this.imageProcessor = new window.CtmImageProcessor();
      this.abortController = new AbortController();
      this.uploadController = null;
      this.resizeObserver = null;
      this.maxDesigns = 10;
      this.maxUploadSize = 20 * 1024 * 1024;
      this.designs = [];
      this.designCardMap = new Map();
      this.activeDesignId = '';
      this.styleOptions = {
        drapery: [
          { label: 'Pinch Pleat', adobeName: 'pinch-pleated-tr', slots: ['wc', 'trim','bb'] },
          { label: 'French Pleat', adobeName: 'french-pleat-tr', slots: ['wc', 'trim', 'bb'] },
          { label: 'Tailor Pleat', adobeName: 'tailor-pleat-tr', slots: ['wc', 'trim','bb'] },
          { label: 'Ripple Fold', adobeName: 'ripple-fold-cato-tr', slots: ['wc', 'trim','bb'] },
          { label: 'Pencil Pleat', adobeName: 'pencil-pleat-tr', slots: ['wc', 'trim', 'bb'] },
          { label: 'Rod Pocket', adobeName: 'rod-pocket-tr-nt', slots: ['wc'] },
          { label: 'Grommet', adobeName: 'grommet-tr', slots: ['wc', 'trim','bb'] }
        ],
        roman: [
          { label: 'Plain', adobeName: 'plain-roman-tr', slots: ['wc'] },
          { label: 'Soft Flat', adobeName: 'flat-roman-seamless-tr', slots: ['wc'] },
          { label: 'Relaxed', adobeName: 'soft-fold-relax-tr', slots: ['wc'] },
          { label: 'Deco Fold', adobeName: 'deco-fold-roman-tr', slots: ['wc'] },
          { label: 'Day Night', adobeName: 'day-night-tr', slots: ['wc'] },
          { label: 'Day Night with Valance', adobeName: 'day-night-valance-tr', slots: ['wc'] },
          { label: 'Top Down Bottom Up', adobeName: 'top-down-bottom-tr', slots: ['wc'] }
        ],
        valance: [
          { label: 'Tailored Valance', adobeName: 'vn1', slots: ['wc'] },
          { label: 'Flat Roman Valance', adobeName: 'vn7', slots: ['wc'] },
          { label: 'Shaped Valance', adobeName: 'vn4', slots: ['wc'] },
          { label: 'Center Pleated Valance', adobeName: 'vn2', slots: ['wc'] },
          { label: 'Scalloped Valance', adobeName: 'vn5', slots: ['wc'] },
          { label: 'Flat Cornice Board', adobeName: 'cn1', slots: ['wc'] },
          { label: 'Arched Cornice Board', adobeName: 'cn2', slots: ['wc'] },
          { label: 'Stepped Cornice Board', adobeName: 'cn3', slots: ['wc'] }
        ]
      };
    }

    connectedCallback() {
      this.fileInput = this.querySelector('[data-design-file]');
      if (!this.fileInput || this.dataset.initialized === 'true') return;

      if (this.abortController.signal.aborted) this.abortController = new AbortController();
      this.dataset.initialized = 'true';

      this.form = this.querySelector('[data-design-form]');
      this.heading = this.querySelector('.ctm-ds__heading');
      this.workbench = this.querySelector('.ctm-ds__workbench');
      this.canvas = this.querySelector('[data-canvas]');
      this.canvasPanel = this.querySelector('.ctm-ds__canvas-panel');
      this.canvasToolbar = this.querySelector('.ctm-ds__canvas-toolbar');
      this.canvasMeta = this.querySelector('.ctm-ds__canvas-meta');
      this.imagePreview = this.querySelector('[data-image-preview]');
      this.previewImage = this.querySelector('[data-preview-image]');
      this.previewButtons = Array.from(this.querySelectorAll('[data-preview-view]'));
      this.previewLabel = this.querySelector('[data-preview-label]');
      this.fitPreviewButton = this.querySelector('[data-fit-preview]');
      this.previewNote = this.querySelector('[data-preview-note]');
      this.formStatus = this.querySelector('[data-form-status]');
      this.styleSelect = this.querySelector('[data-treatment-style]');
      this.styleLabel = this.querySelector('[data-style-label]');
      this.scaleRange = this.querySelector('[data-scale-range]');
      this.scaleOutput = this.querySelector('[data-scale-output]');
      this.scaleGroup = this.querySelector('.ctm-ds__scale-group');
      this.scaleHelpTrigger = this.querySelector('[data-scale-help-trigger]');
      this.scaleHelpPanel = this.querySelector('[data-scale-help]');
      this.reviewButton = this.querySelector('[data-submit-review]');
      this.termsInput = this.querySelector('[data-terms-agreement]');
      this.designList = this.querySelector('[data-design-list]');
      this.designCount = this.querySelector('[data-design-count]');
      this.libraryUploadButton = this.querySelector('.ctm-ds__replace');
      this.intentCopy = this.querySelector('[data-intent-copy]');
      this.termsLink = this.querySelector('[data-terms-link]');
      this.termsText = this.querySelector('[data-terms-text]');
      this.uploadTriggers = Array.from(this.querySelectorAll('[data-upload-trigger]'));
      this.uploadFeedbacks = Array.from(this.querySelectorAll('[data-upload-feedback]'));
      this.intentInputs = Array.from(this.querySelectorAll('[data-design-intent]'));
      this.familyInputs = Array.from(this.querySelectorAll('input[data-family]'));
      this.requireAgreement = this.dataset.requireAgreement !== 'false';
      this.previewMode = 'room';
      this.scaleHelpOpen = false;

      this.bindEvents();
      this.observeLayout();
      this.fillStyleOptions('drapery', '');
      this.a11y.syncIntentInputs(this.intentInputs, 'private', this.intentCopy, this.termsLink, this.termsText);
      this.a11y.syncFamilyInputs(this.familyInputs, 'drapery');
      this.syncScaleHelp(false);
      this.syncUIState();
      window.requestAnimationFrame(() => this.syncLayoutHeights());
    }

    disconnectedCallback() {
      this.abortController.abort();
      this.uploadController?.abort();
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      delete this.dataset.initialized;
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.uploadTriggers.forEach((button) => {
        button.addEventListener('click', () => this.requestUpload(), { signal });
      });

      this.fileInput.addEventListener('change', () => this.handleFile(), { signal });
      this.previewButtons.forEach((button) => {
        button.addEventListener('click', () => this.setPreviewMode(button.dataset.previewView), { signal });
      });
      this.fitPreviewButton?.addEventListener('click', () => this.fitPreview(), { signal });
      this.styleSelect.addEventListener('change', () => this.updateTreatmentStyle(), { signal });

      this.intentInputs.forEach((input) => {
        input.addEventListener('change', () => this.setIntent(input.value), { signal });
      });

      this.familyInputs.forEach((input) => {
        input.addEventListener('change', () => this.setFamily(input.value), { signal });
      });

      this.scaleRange.addEventListener('input', () => this.updateScale(this.scaleRange.value), { signal });
      this.scaleHelpTrigger?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.toggleScaleHelp();
      }, { signal });
      this.scaleHelpPanel?.addEventListener('click', (event) => event.stopPropagation(), { signal });
      document.addEventListener('click', () => {
        if (!this.scaleHelpOpen) return;
        this.closeScaleHelp();
      }, { signal });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.scaleHelpOpen) return;
        this.closeScaleHelp();
        this.scaleHelpTrigger?.focus();
      }, { signal });
      this.termsInput.addEventListener('change', () => this.syncUIState(), { signal });

      this.reviewButton.addEventListener('click', () => this.submitForReview(), { signal });
      this.form.addEventListener('submit', (event) => event.preventDefault(), { signal });
      window.addEventListener('resize', () => this.syncLayoutHeights(), { signal });
    }

    toggleScaleHelp() {
      this.syncScaleHelp(!this.scaleHelpOpen);
    }

    closeScaleHelp() {
      this.syncScaleHelp(false);
    }

    syncScaleHelp(isOpen = false) {
      this.scaleHelpOpen = isOpen;
      this.a11y.syncScaleHelp({
        group: this.scaleGroup,
        trigger: this.scaleHelpTrigger,
        panel: this.scaleHelpPanel
      }, isOpen);
    }

    observeLayout() {
      if (!window.ResizeObserver || !this.heading) return;

      this.resizeObserver = new ResizeObserver(() => this.syncLayoutHeights());
      [this.heading, this.canvasToolbar, this.canvasMeta].filter(Boolean).forEach((element) => {
        this.resizeObserver.observe(element);
      });
    }

    requestUpload() {
      if (!this.canUpload()) {
        this.setUploadFeedback('You can keep up to 10 designs in this workspace.');
        return;
      }
      this.resetUploadFeedback();
      this.setPreviewNoteText(true);
      this.syncCanvasMinHeight();
      this.fileInput.click();
    }

    canUpload() {
      return this.designs.length < this.maxDesigns && !this.uploadController;
    }

    getActiveDesign() {
      return this.designs.find((design) => design.id === this.activeDesignId) || null;
    }

    getDesignDisplayName(design) {
      return (design?.name || '').trim() || 'Untitled design';
    }

    getSelectedIntent() {
      return this.intentInputs.find((input) => input.checked)?.value || 'private';
    }

    getSelectedFamily() {
      return this.familyInputs.find((input) => input.checked)?.value || 'drapery';
    }

    getRootState() {
      const hasDesigns = this.designs.length > 0;
      const atLimit = this.designs.length >= this.maxDesigns;
      return {
        hasDesigns,
        atLimit,
        isUploading: Boolean(this.uploadController)
      };
    }

    setPreviewMode(mode) {
      if (!['pattern', 'room'].includes(mode)) return;
      this.previewMode = mode;
      this.a11y.syncPreviewButtons(this.previewButtons, mode);
      this.renderActiveDesign();
    }

    getRoomPreviewUrl(design) {
      const fileKey = design?.fileKey?.replace(/^user-fabric\//, '');
      const adobeName = this.getAdobeName(design?.treatmentStyle);
      const previewQuery = this.getRoomPreviewQuery(fileKey, adobeName, design?.scale);
      if (!adobeName || !previewQuery) return '';
      return `https://s7d6.scene7.com/ir/render/TWOPAGESCURTAINS/${adobeName}?${previewQuery}`;
    }

    getRoomPreviewQuery(fileKey, adobeName, scale = 70) {
      if (!fileKey) return '';

      const slots = this.getTreatmentSlots(adobeName);
      if (!slots.includes('wc')) return '';

      const previewResolution = Math.max(25, Math.min(200, Math.round(Number(scale) || 70)));
      const params = ['hei=1200', 'resmode=sharp', 'qlt=80,1'];
      params.push('obj=wc', `src=%7B${encodeURIComponent(fileKey)}%7D`, `res=${previewResolution}`);

      if (slots.includes('trim')) {
        params.push('obj=trim', 'hide=1');
      }

      if (slots.includes('bb')) {
        params.push('obj=bb', 'hide=1');
      }

      return params.join('&');
    }

    getPreviewImageUrl(design) {
      if (this.previewMode === 'room') return this.getRoomPreviewUrl(design) || design?.uploadedImageUrl || '';
      return design?.uploadedImageUrl || '';
    }

    syncUIState() {
      const state = this.getRootState();
      this.a11y.syncRootState(state);
      this.a11y.syncUploadControls({
        fileInput: this.fileInput,
        uploadTriggers: this.uploadTriggers,
        libraryUploadButton: this.libraryUploadButton,
        isBusy: state.isUploading,
        atLimit: state.atLimit,
        hasDesigns: state.hasDesigns
      });
      this.a11y.syncCanvas({
        canvas: this.canvas,
        imagePreview: this.imagePreview,
        hasDesigns: state.hasDesigns,
        isBusy: state.isUploading
      });
      this.a11y.syncDesignAvailability({
        scaleRange: this.scaleRange,
        fitPreviewButton: this.fitPreviewButton,
        reviewButton: this.reviewButton,
        termsInput: this.termsInput
      }, state.hasDesigns, this.getSelectedSubmitUrl(), this.requireAgreement);
      this.a11y.syncDesignCount(this.designCount, this.designs.length);
      this.syncPreviewLabel();
      this.syncPreviewNote(state.hasDesigns || state.isUploading);
      this.syncCanvasMinHeight();
    }

    captureActiveDesignState() {
      const design = this.getActiveDesign();
      if (!design) return;

      design.intent = this.getSelectedIntent();
      design.family = this.getSelectedFamily();
      design.treatmentStyle = this.styleSelect.value;
      design.scale = Math.max(25, Math.min(200, Math.round(Number(this.scaleRange.value) || 70)));
    }

    createId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `design-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    createDesign(file, image, imageUrl, fileKey) {
      const scale = 70;
      const family = this.getSelectedFamily();
      const treatmentStyle = this.fillStyleOptions(family, this.styleSelect.value);
      return {
        id: this.createId(),
        uploadedImageUrl: imageUrl,
        fileKey,
        name: file.name.replace(/\.[^.]+$/, '') || 'Untitled design',
        sourceWidth: image.naturalWidth,
        sourceHeight: image.naturalHeight,
        scale,
        family,
        treatmentStyle,
        intent: this.getSelectedIntent()
      };
    }

    async handleFile() {
      let file = this.fileInput.files?.[0];
      if (!file) return;

      if (this.designs.length >= this.maxDesigns) {
        this.setUploadFeedback('You can keep up to 10 designs in this workspace.');
        this.fileInput.value = '';
        this.syncUIState();
        return;
      }

      if (file.size > this.maxUploadSize) {
        this.setUploadFeedback(this.getFileTooLargeMessage());
        this.fileInput.value = '';
        return;
      }

      if (!this.imageProcessor.isSupported(file)) {
        this.setUploadFeedback(this.imageProcessor.messages.unsupportedType);
        this.fileInput.value = '';
        return;
      }

      const ctmFetch = window.theme?.utils?.ctmFetch;
      const ctmUploadFetch = window.theme?.utils?.ctmUploadFetch;
      if (typeof ctmFetch !== 'function' || typeof ctmUploadFetch !== 'function') {
        this.setUploadFeedback('The image upload service is not configured.');
        this.fileInput.value = '';
        return;
      }

      this.uploadController?.abort();
      const uploadController = new AbortController();
      this.uploadController = uploadController;
      this.syncUIState();
      this.resetUploadFeedback();

      try {
        file = await this.imageProcessor.prepare(file, { signal: uploadController.signal });
        if (!this.isConnected || uploadController.signal.aborted || uploadController !== this.uploadController) return;
        const presignBody = await ctmFetch('/file/presign-upload', {
          method: 'POST',
          json: {
            fileName: file.name,
            contentType: file.type,
            businessType: 'adobe',
            shopifyCustomerId: this.dataset.customerId || ''
          },
          signal: uploadController.signal
        });

        if (presignBody?.code !== 200) {
          throw new Error(presignBody?.message || 'Unable to prepare the image upload.');
        }

        const uploadData = presignBody?.data;
        if (!uploadData?.uploadUrl || !uploadData?.fileUrl || !uploadData?.fileKey) {
          throw new Error('The image upload service did not return upload details.');
        }

        const uploadHeaders = {
          ...(uploadData.headers || {}),
          'Content-Type': uploadData.headers?.['Content-Type'] || file.type
        };

        const uploadResponse = await ctmUploadFetch(uploadData.uploadUrl, {
          headers: uploadHeaders,
          body: file,
          signal: uploadController.signal
        });

        if (!uploadResponse.ok) {
          throw new Error(`Image upload failed (${uploadResponse.status}).`);
        }

        const imageUrl = uploadData.fileUrl;
        const image = await this.imageProcessor.loadImage(imageUrl);
        if (!this.isConnected || uploadController.signal.aborted || uploadController !== this.uploadController) return;

        const design = this.createDesign(file, image, imageUrl, uploadData.fileKey);

        this.captureActiveDesignState();
        this.designs.push(design);
        this.designCardMap.set(design.id, this.renderDesignCard(design, this.designs.length - 1));
        this.designList.append(this.designCardMap.get(design.id));
        this.activeDesignId = design.id;
        this.updateDesignCards();
        this.renderActiveDesign();
        this.resetUploadFeedback();
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (!this.designs.length) this.a11y.syncPreviewImage(this.previewImage, null, false);
        this.setUploadFeedback(error.message || 'Unable to upload this image. Please try again.');
      } finally {
        if (uploadController === this.uploadController) {
          this.uploadController = null;
          this.syncUIState();
        }
        this.fileInput.value = '';
        this.syncUIState();
      }
    }

    getFileTooLargeMessage() {
      return 'The selected file is too large. Maximum size is 20MB.';
    }

    renderDesignCard(design, index) {
      const card = document.createElement('div');
      card.className = 'ctm-ds__design-card';
      card.dataset.designId = design.id;

      const previewButton = document.createElement('button');
      previewButton.type = 'button';
      previewButton.className = 'ctm-ds__design-card-preview';
      previewButton.dataset.designSelect = '';

      const image = document.createElement('img');
      image.src = design.uploadedImageUrl;
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');
      image.dataset.designThumbnail = '';

      const copy = document.createElement('span');
      const name = document.createElement('strong');
      const status = document.createElement('small');
      copy.append(name, status);
      previewButton.append(image, copy);

      const deleteButton = document.createElement('button');
      deleteButton.type = 'button';
      deleteButton.className = 'ctm-ds__design-delete';
      deleteButton.dataset.designDelete = '';
      deleteButton.append(this.createDeleteIcon());

      previewButton.addEventListener('click', () => this.selectDesign(design.id));
      deleteButton.addEventListener('click', () => this.removeDesign(design.id));

      card.append(previewButton, deleteButton);

      this.updateDesignCard(design, index, card);
      return card;
    }

    createDeleteIcon() {
      const svgNamespace = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(svgNamespace, 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');

      const paths = [
        'M18 6 6 18',
        'M6 6l12 12'
      ];

      paths.forEach((pathData) => {
        const path = document.createElementNS(svgNamespace, 'path');
        path.setAttribute('d', pathData);
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-width', '1.8');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        svg.append(path);
      });

      return svg;
    }

    updateDesignCards() {
      const total = this.designs.length;
      this.designs.forEach((design, index) => {
        const card = this.designCardMap.get(design.id);
        if (!card) return;
        this.updateDesignCard(design, index, card, total);
      });
    }

    updateDesignCard(design, index = 0, card = this.designCardMap.get(design.id), total = this.designs.length) {
      if (!card) return;
      const isActive = design.id === this.activeDesignId;
      const displayName = this.getDesignDisplayName(design);
      const strong = card.querySelector('strong');
      const status = card.querySelector('small');
      const thumbnail = card.querySelector('img');

      this.a11y.syncActiveDesignCard(card, { active: isActive, displayName, index, total });

      if (strong) strong.textContent = displayName;
      if (status) status.textContent = isActive ? 'Selected' : `Design ${index + 1} of ${total}`;
      if (thumbnail) thumbnail.alt = `Preview of ${displayName}`;
    }

    selectDesign(id) {
      if (!id || this.activeDesignId === id) {
        this.updateDesignCards();
        this.renderActiveDesign();
        return;
      }

      this.captureActiveDesignState();
      this.activeDesignId = id;
      this.renderActiveDesign();
      this.updateDesignCards();
    }

    removeDesign(id) {
      const removeIndex = this.designs.findIndex((design) => design.id === id);
      if (removeIndex < 0) return;

      const wasActive = this.activeDesignId === id;
      const card = this.designCardMap.get(id);
      card?.remove();
      this.designCardMap.delete(id);
      this.designs.splice(removeIndex, 1);

      const focusDesign = this.designs[Math.min(removeIndex, this.designs.length - 1)] || null;

      if (wasActive) {
        this.activeDesignId = focusDesign?.id || '';
        this.renderActiveDesign();
      } else {
        this.updateDesignCards();
        this.syncUIState();
      }

      window.requestAnimationFrame(() => this.focusAfterDesignRemoval(focusDesign));
    }

    focusAfterDesignRemoval(focusDesign) {
      const nextCard = focusDesign ? this.designCardMap.get(focusDesign.id) : null;
      const nextButton = nextCard?.querySelector('[data-design-select]');
      if (nextButton) {
        nextButton.focus();
        return;
      }

      const uploadButton = this.uploadTriggers.find((button) => !button.hidden && !button.disabled);
      uploadButton?.focus();
    }

    renderActiveDesign() {
      const design = this.getActiveDesign();
      if (!design) {
        this.syncUIState();
        this.a11y.syncPreviewImage(this.previewImage, null, false);
        this.scaleOutput.value = '70%';
        this.syncPreviewLabel();
        this.syncCanvasMinHeight();
        return;
      }

      this.a11y.syncPreviewImage(this.previewImage, design, true, this.getPreviewImageUrl(design));
      this.a11y.syncIntentInputs(this.intentInputs, design.intent, this.intentCopy, this.termsLink, this.termsText);
      this.a11y.syncFamilyInputs(this.familyInputs, design.family);
      this.fillStyleOptions(design.family, design.treatmentStyle);
      design.treatmentStyle = this.styleSelect.value;
      this.scaleRange.value = design.scale;
      this.scaleOutput.value = `${design.scale}%`;
      this.syncUIState();
      this.syncCanvasMinHeight();
    }

    fillStyleOptions(family, selectedValue) {
      const fragment = document.createDocumentFragment();
      const options = this.styleOptions[family] || this.styleOptions.drapery;
      const selectedPath = this.getAdobeName(selectedValue);
      const optionPaths = options.map((item) => item.adobeName);

      options.forEach((item) => {
        const option = document.createElement('option');
        option.value = item.adobeName;
        option.textContent = item.label;
        fragment.append(option);
      });

      this.styleSelect.replaceChildren(fragment);

      const labels = {
        drapery: 'Header Style',
        roman: 'Fold Style',
        valance: 'Style'
      };
      this.styleLabel.textContent = labels[family] || labels.drapery;

      const nextValue = optionPaths.includes(selectedPath) ? selectedPath : optionPaths[0];
      this.styleSelect.value = nextValue;
      this.syncPreviewLabel({ family, treatmentStyle: nextValue });
      return nextValue;
    }

    getTreatmentPath(value = '') {
      return String(value).trim().toLowerCase().replace(/\s+/g, '-');
    }

    getAdobeName(value = '') {
      return this.getTreatmentOption(value)?.adobeName || this.getTreatmentPath(value);
    }

    getTreatmentOption(value = '') {
      const treatmentPath = this.getTreatmentPath(value);
      return Object.values(this.styleOptions)
        .flat()
        .find((item) => item.adobeName === treatmentPath || this.getTreatmentPath(item.label) === treatmentPath) || null;
    }

    getTreatmentSlots(value = '') {
      return this.getTreatmentOption(value)?.slots || [];
    }

    getFamilyLabel(value = '') {
      const labels = {
        drapery: 'Drapery',
        roman: 'Roman Shades',
        valance: 'Valances & Cornices'
      };
      return labels[value] || labels.drapery;
    }

    getTreatmentLabel(value = '') {
      const option = this.getTreatmentOption(value);
      return option?.label || this.styleSelect?.selectedOptions?.[0]?.textContent?.trim() || '';
    }

    syncPreviewLabel(state = {}) {
      if (!this.previewLabel) return;
      const design = this.getActiveDesign();
      const family = state.family || design?.family || this.getSelectedFamily();
      const treatmentStyle = state.treatmentStyle || design?.treatmentStyle || this.styleSelect?.value || '';
      const treatmentLabel = this.getTreatmentLabel(treatmentStyle);
      this.previewLabel.textContent = treatmentLabel
        ? `${this.getFamilyLabel(family)} · ${treatmentLabel}`
        : this.getFamilyLabel(family);
    }

    setPreviewNoteText(hasDesigns = false) {
      if (!this.previewNote) return;
      const noteKey = hasDesigns ? 'readyText' : 'emptyText';
      const nextText = this.previewNote.dataset[noteKey] || '';
      if (this.previewNote.textContent === nextText) return false;

      this.previewNote.textContent = nextText;
      return true;
    }

    syncPreviewNote(hasDesigns = false) {
      const hasChanged = this.setPreviewNoteText(hasDesigns);
      if (hasChanged) this.syncCanvasMinHeight();
    }

    setIntent(value) {
      const design = this.getActiveDesign();
      this.a11y.syncIntentInputs(this.intentInputs, value, this.intentCopy, this.termsLink, this.termsText);
      if (design) {
        design.intent = value;
        this.updateDesignCards();
      }
      this.syncUIState();
    }

    setFamily(value) {
      const design = this.getActiveDesign();
      this.a11y.syncFamilyInputs(this.familyInputs, value);
      const treatmentStyle = this.fillStyleOptions(value, design?.treatmentStyle || this.styleSelect.value);
      if (design) {
        design.family = value;
        design.treatmentStyle = treatmentStyle;
        this.updateDesignCards();
        if (this.previewMode === 'room') this.a11y.syncPreviewImage(this.previewImage, design, true, this.getPreviewImageUrl(design));
      }
      this.syncPreviewLabel({ family: value, treatmentStyle });
    }

    updateTreatmentStyle() {
      const design = this.getActiveDesign();
      this.syncPreviewLabel({ treatmentStyle: this.styleSelect.value });
      if (!design) return;
      design.treatmentStyle = this.styleSelect.value;
      if (this.previewMode === 'room') this.a11y.syncPreviewImage(this.previewImage, design, true, this.getPreviewImageUrl(design));
    }

    fitPreview() {
      this.updateScale(100);
    }

    updateScale(value) {
      const design = this.getActiveDesign();
      if (!design || !design.sourceWidth || !design.sourceHeight) return;

      const scale = Math.max(25, Math.min(200, Math.round(Number(value) || 70)));
      design.scale = scale;
      this.scaleRange.value = scale;
      this.scaleOutput.value = `${scale}%`;
      if (this.previewMode === 'room') this.a11y.syncPreviewImage(this.previewImage, design, true, this.getPreviewImageUrl(design));
    }

    submitForReview() {
      if (!this.getActiveDesign()) return;
      if (this.requireAgreement && !this.termsInput.checked) {
        this.setFormStatus('Please agree to the Terms of Service before submitting.', 'error');
        return;
      }

      const submitUrl = this.getSelectedSubmitUrl();
      if (!submitUrl) {
        return;
      }

      window.location.assign(submitUrl);
    }

    getSelectedSubmitUrl() {
      const intent = this.getSelectedIntent();
      const datasetKey = intent === 'public' ? 'publicSubmitUrl' : 'privateSubmitUrl';
      return this.dataset[datasetKey]?.trim() || '';
    }

    setUploadFeedback(message, state = 'error') {
      const feedbackContext = this.designs.length > 0 ? 'library' : 'empty';
      this.uploadFeedbacks.forEach((region) => {
        const isActiveRegion = region.dataset.uploadFeedback === feedbackContext;
        region.hidden = !isActiveRegion;
        this.a11y.syncStatus(region, isActiveRegion ? message : '', isActiveRegion ? state : '');
      });
      this.syncCanvasMinHeight();
    }

    resetUploadFeedback() {
      this.uploadFeedbacks.forEach((region) => {
        region.hidden = true;
        this.a11y.syncStatus(region, '');
      });
      this.syncCanvasMinHeight();
    }

    setFormStatus(message, state = '') {
      this.a11y.syncStatus(this.formStatus, message, state);
      this.syncCanvasMinHeight();
    }

    syncCanvasMinHeight() {
      if (!this.canvas || !this.isConnected) return;

      this.syncWorkbenchHeight();

      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const canvasRect = this.canvas.getBoundingClientRect();
      let minHeight = Math.max(0, Math.round(canvasRect.width || 0));

      if (!isMobile) {
        const canvasPanelRectHeight = this.canvasPanel?.getBoundingClientRect().height || 0;
        const workbenchHeight = this.workbench?.clientHeight || canvasPanelRectHeight;
        const canvasPanelHeight = Math.min(canvasPanelRectHeight, workbenchHeight);
        const canvasToolbarHeight = this.getLayoutHeight(this.canvasToolbar);
        const canvasMetaHeight = this.getLayoutHeight(this.canvasMeta);
        const canvasPanelStyle = this.canvasPanel ? window.getComputedStyle(this.canvasPanel) : null;
        const canvasPanelPaddingTop = canvasPanelStyle ? Number.parseFloat(canvasPanelStyle.paddingTop) || 0 : 0;
        const canvasPanelPaddingBottom = canvasPanelStyle ? Number.parseFloat(canvasPanelStyle.paddingBottom) || 0 : 0;
        const availableHeight = Math.max(
          0,
          Math.floor(canvasPanelHeight - canvasToolbarHeight - canvasMetaHeight - canvasPanelPaddingTop - canvasPanelPaddingBottom)
        );
        minHeight = Math.min(minHeight, availableHeight);
      }

      this.style.setProperty('--ctm-ds-canvas-min-height', `${minHeight}px`);
    }

    getLayoutHeight(element) {
      if (!element) return 0;
      const rectHeight = element.getBoundingClientRect().height || 0;
      const contentHeight = element.scrollHeight || 0;
      return Math.ceil(Math.max(rectHeight, contentHeight));
    }

    syncWorkbenchHeight() {
      if (!this.heading || !this.workbench || !this.isConnected) return;

      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      if (!isDesktop) return;

      const headingHeight = this.heading.getBoundingClientRect().height || 0;
      const workbenchHeight = Math.max(0, Math.floor(window.innerHeight - headingHeight));
      this.style.setProperty('--ctm-ds-workbench-height', `${workbenchHeight}px`);
    }

    syncLayoutHeights() {
      this.syncWorkbenchHeight();
      this.syncCanvasMinHeight();
    }
  }

  customElements.define('ctm-design-studio', CtmDesignStudio);

  window.addEventListener('pageshow', (event) => {
    console.log('no login ---')
    console.log({
      persisted: event.persisted,
      auth: Boolean(document.querySelector('ctm-design-studio .ctm-ds__auth')),
      navigationType: performance.getEntriesByType('navigation')[0]?.type
    });
    if (!event.persisted || !document.querySelector('ctm-design-studio .ctm-ds__auth')) return;
    console.log('reloading page');
    window.location.reload();
  });
}
